/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useState, useEffect } from "react"; // Import hooks
import PropTypes from "prop-types"; // Import PropTypes
import { Redirect, Route } from "react-router-dom";
import { AuthConsumer } from "./AuthContext";
import RenderFullPageLoading from "./assets/component/RenderFullPageLoading.js";
import IdleTimeout from "./assets/component/IdleTimeout.js";
import { simpleFetch } from "./restWrapper"; // Import simpleFetch

// Helper function to manage body classes
const setBodyOrgTypeClass = (orgType) => {
  document.body.classList.remove("org-type-business", "org-type-nonprofit");
  if (orgType === "business") {
    document.body.classList.add("org-type-business");
  } else if (orgType === "nonprofit") {
    document.body.classList.add("org-type-nonprofit");
  }
};

function ProtectedOrgRouteInternal({
  component: Component,
  authProps,
  ...rest
}) {
  const { t, isOrgAuth, orgLogout, language, i18n } = authProps;
  const [organizationObj, setOrganizationObj] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Add loading state

  useEffect(() => {
    let isMounted = true; // Flag to prevent state update on unmounted component
    if (isOrgAuth) {
      setIsLoading(true);
      // Fetch organization data when authenticated
      simpleFetch("/organizations/", "GET") // Assuming GET /organizations/ returns the logged-in org user data
        .then((res) => {
          // Replace optional chaining with standard check for ESLint compatibility
          if (
            isMounted &&
            res.status === 200 &&
            res.body &&
            res.body.organizationObj
          ) {
            setOrganizationObj(res.body.organizationObj);
            setBodyOrgTypeClass(res.body.organizationObj.organizationType);
          } else {
            // Handle error or case where org data isn't found
            setBodyOrgTypeClass(null); // Clear class if fetch fails
          }
        })
        .catch(() => {
          if (isMounted) setBodyOrgTypeClass(null); // Clear class on error
        })
        .finally(() => {
          if (isMounted) setIsLoading(false);
        });
    } else {
      // Clear data and class if not authenticated
      setOrganizationObj(null);
      setBodyOrgTypeClass(null);
      setIsLoading(false);
    }

    // Cleanup function
    return () => {
      isMounted = false;
      setBodyOrgTypeClass(null); // Remove class on unmount/auth change
    };
  }, [isOrgAuth]); // Re-run effect if auth status changes

  if (!isOrgAuth) {
    return <Redirect to="/OrgLogin" />;
  }

  if (isLoading) {
    return <RenderFullPageLoading />; // Show loading indicator while fetching data
  }

  // Prepare props for the wrapped component
  // Refactor path processing to avoid indentation issues with chained methods
  let pathName =
    rest.location && rest.location.pathname ? rest.location.pathname : "";
  let pathSegments = pathName.substring(1).split("/");
  const currentPath = (pathSegments[0] || "DEFAULT").toUpperCase();
  const languageObj = t(currentPath, { returnObjects: true });

  return (
    <React.Fragment>
      {/* Warn-then-logout on inactivity (same pattern as the Admin
          dashboard). Mounted here so every org page gets it, and the logout
          is the clean AuthContext one — not the wipe-and-reload the app
          falls back to when the backend session expires on its own. */}
      <IdleTimeout
        onLogout={orgLogout}
        languageObj={t("IDLETIMEOUT", { returnObjects: true })}
      />
      <Component
        {...rest} // Pass router props like location, history, match
        i18n={i18n}
        orgLogout={orgLogout}
        language={language}
        languageObj={languageObj}
        organizationObj={organizationObj} // Pass fetched org data
        RenderFullPageLoading={RenderFullPageLoading}
      />
    </React.Fragment>
  );
}

// Add prop types for validation
ProtectedOrgRouteInternal.propTypes = {
  component: PropTypes.elementType.isRequired,
  authProps: PropTypes.object.isRequired,
  location: PropTypes.object, // From Route render prop
};

// Main ProtectedOrgRoute component consumes AuthContext and passes props
const ProtectedOrgRoute = ({ component: Component, ...rest }) => (
  <AuthConsumer>
    {(authProps) => (
      <Route
        {...rest}
        render={(routeProps) => (
          <ProtectedOrgRouteInternal
            component={Component}
            authProps={authProps}
            {...routeProps} // Pass router props down
          />
        )}
      />
    )}
  </AuthConsumer>
);

// Add prop types for validation
ProtectedOrgRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
};

export default ProtectedOrgRoute;
